import { useState, useEffect, useCallback, useRef } from 'react';

import { clampInteger } from 'utils/clampInteger';

import { useAiCompanionSidebarInternal } from './AiCompanionSidebarContext';
import {
  MIN_SIDEBAR_WIDTH_PIXELS,
  MAX_SIDEBAR_WIDTH_PIXELS,
} from './constants';

/**
 * Drag-to-resize for the AI Companion sidebar.
 * Attaches document-level mousemove/mouseup listeners while dragging,
 * throttles width updates via requestAnimationFrame, and persists
 * the final width to localStorage on mouse-up.
 */
export const useAiCompanionResize = () => {
  const { setSidebarWidth, persistSidebarWidth } =
    useAiCompanionSidebarInternal();
  const [isDragging, setIsDragging] = useState(false);
  const animationFrameRef = useRef<number>();
  const lastWidthRef = useRef(0);

  // Ref so onMouseUp always sees the latest persistSidebarWidth without
  // re-triggering the effect (useLocalStorage's setter isn't stable).
  const persistRef = useRef(persistSidebarWidth);
  persistRef.current = persistSidebarWidth;

  const onResizeHandleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  useEffect(() => {
    if (!isDragging) return undefined;

    const onMouseMove = (e: MouseEvent) => {
      if (animationFrameRef.current)
        cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = requestAnimationFrame(() => {
        const newWidth = clampInteger(
          window.innerWidth - e.clientX,
          MIN_SIDEBAR_WIDTH_PIXELS,
          MAX_SIDEBAR_WIDTH_PIXELS
        );
        lastWidthRef.current = newWidth;
        setSidebarWidth(newWidth);
      });
    };

    const onMouseUp = () => {
      if (animationFrameRef.current)
        cancelAnimationFrame(animationFrameRef.current);
      setIsDragging(false);
      // Guard against mousedown+mouseup with no movement, which would persist 0.
      if (lastWidthRef.current) {
        persistRef.current(lastWidthRef.current);
      }
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);

    document.body.style.cursor = 'col-resize'; // keep resize cursor everywhere while dragging
    document.body.style.userSelect = 'none'; // prevent text selection while dragging

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      if (animationFrameRef.current)
        cancelAnimationFrame(animationFrameRef.current);
    };
  }, [isDragging, setSidebarWidth]);

  return { isDragging, onResizeHandleMouseDown };
};
